import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
/**
 * Risk-authoring actions that don't map onto a single table's CRUD:
 * generating instances from the shared catalogue, assigning translation
 * groups, bulk-linking measures, and the history every risk template and
 * instance panel shows. Each is a domain-data operation; the handlers map its
 * refusals to HTTP.
 */
export const AvailableRiskSourceType = S.Literal("template", "instance");
export const AvailableRiskIdInput = S.Struct({
    id: S.String,
    source_type: AvailableRiskSourceType,
});
export const GenerateRiskInstancesParamsPayload = S.Struct({
    available_risk_ids: S.NonEmptyArray(AvailableRiskIdInput),
    location_ids: S.optionalWith(S.Array(S.UUID), { default: () => [] }),
    is_company_wide: S.Boolean,
    client_id: S.UUID,
    language: S.Literal("de-DE", "en-US"),
});
export const PotentialRiskInstanceView = S.Struct({
    area: S.NullOr(S.String),
    object: S.NullOr(S.String),
    activity: S.NullOr(S.String),
    factor: S.NullOr(S.String),
    subfactor: S.NullOr(S.String),
});
export const PotentialRiskInstance = S.Struct({
    id: S.String,
    title: S.String,
    description: S.optional(S.String),
    location_id: S.optional(S.UUID),
    location_name: S.optional(S.String),
    is_company_wide: S.Boolean,
    area: S.optional(S.String),
    object: S.optional(S.String),
    activity: S.optional(S.String),
    factor: S.optional(S.String),
    subfactor: S.optional(S.String),
    source: S.optional(S.String),
    mode: S.optional(S.String),
    severity: S.Int,
    probability: S.Int,
    status: S.String,
    source_type: AvailableRiskSourceType,
    source_title: S.String,
    duplicate_indicator: S.optional(S.String),
    view: PotentialRiskInstanceView,
});
export const GenerateRiskInstancesPreviewResult = S.Struct({
    potential_instances: S.Array(PotentialRiskInstance),
    summary: S.Struct({
        total: S.Int,
        from_templates: S.Int,
        from_instances: S.Int,
    }),
});
export const GenerateRiskInstancesPreview = HttpApiEndpoint.post("generateRiskInstancesPreview", "/admin/risk/instances/generate/preview")
    .setPayload(GenerateRiskInstancesParamsPayload)
    .addSuccess(S.Struct({ data: GenerateRiskInstancesPreviewResult }))
    .addError(Forbidden, { status: 403 });
export const CreateRiskInstancesFromGenerationResult = S.Struct({
    success: S.Literal(true),
    created: S.Int,
    instances: S.Array(S.Unknown),
});
export const CreateRiskInstancesFromGeneration = HttpApiEndpoint.post("createRiskInstancesFromGeneration", "/admin/risk/instances/generate/create")
    .setPayload(GenerateRiskInstancesParamsPayload)
    .addSuccess(S.Struct({ data: CreateRiskInstancesFromGenerationResult }))
    .addError(Forbidden, { status: 403 });
export const AssignRiskInstancesPayload = S.Struct({
    translation_group_ids: S.NonEmptyArray(S.Int),
    assignment_purpose: S.NonEmptyString,
    assignment_description: S.NonEmptyString,
});
export const AssignRiskInstancesResult = S.Struct({
    success: S.Literal(true),
    assigned: S.Int,
    translation_groups: S.Int,
});
export const AssignRiskInstances = HttpApiEndpoint.post("assignRiskInstances", "/admin/risk/instances/assign")
    .setPayload(AssignRiskInstancesPayload)
    .addSuccess(S.Struct({ data: AssignRiskInstancesResult }))
    .addError(Forbidden, { status: 403 });
export const BulkCreateInstanceMeasureLinksPayload = S.Struct({
    risk_instance_ids: S.NonEmptyArray(S.UUID),
    measure_template_ids: S.NonEmptyArray(S.UUID),
});
export const BulkCreateInstanceMeasureLinksResult = S.Struct({
    success: S.Literal(true),
    created: S.Int,
    skipped: S.Int,
    total: S.Int,
});
export const BulkCreateInstanceMeasureLinks = HttpApiEndpoint.post("bulkCreateInstanceMeasureLinks", "/admin/risk/instance-measure-links/bulk")
    .setPayload(BulkCreateInstanceMeasureLinksPayload)
    .addSuccess(S.Struct({ data: BulkCreateInstanceMeasureLinksResult }))
    .addError(Forbidden, { status: 403 });
export const RiskActivityUser = S.Struct({
    first_name: S.NullOr(S.String),
    last_name: S.NullOr(S.String),
});
export const RiskActivityEntry = S.Struct({
    timestamp: S.String,
    activity_type: S.String,
    user_first_name: S.NullOr(S.String),
    user_last_name: S.NullOr(S.String),
    changed_fields: S.Array(S.String),
});
export const RiskActivityNotFound = S.Struct({
    _tag: S.Literal("RiskActivityNotFound"),
});
export const GetRiskTemplateActivityParams = S.Struct({
    id: S.UUID,
    limit: S.optionalWith(S.NumberFromString.pipe(S.int(), S.between(1, 250)), {
        default: () => 250,
    }),
    offset: S.optionalWith(S.NumberFromString.pipe(S.int(), S.nonNegative()), {
        default: () => 0,
    }),
});
export const GetRiskTemplateActivity = HttpApiEndpoint.get("getRiskTemplateActivity", "/admin/risk/templates/activity")
    .setUrlParams(GetRiskTemplateActivityParams)
    .addSuccess(S.Struct({ data: S.Array(RiskActivityEntry) }))
    .addError(RiskActivityNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const GetRiskInstanceActivityParams = S.Struct({
    id: S.UUID,
    limit: S.optionalWith(S.NumberFromString.pipe(S.int(), S.between(1, 250)), {
        default: () => 250,
    }),
    offset: S.optionalWith(S.NumberFromString.pipe(S.int(), S.nonNegative()), {
        default: () => 0,
    }),
});
export const GetRiskInstanceActivity = HttpApiEndpoint.get("getRiskInstanceActivity", "/admin/risk/instances/activity")
    .setUrlParams(GetRiskInstanceActivityParams)
    .addSuccess(S.Struct({ data: S.Array(RiskActivityEntry) }))
    .addError(RiskActivityNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const adminRiskActionsGroup = HttpApiGroup.make("admin-risk-actions")
    .add(GenerateRiskInstancesPreview)
    .add(CreateRiskInstancesFromGeneration)
    .add(AssignRiskInstances)
    .add(BulkCreateInstanceMeasureLinks)
    .add(GetRiskTemplateActivity)
    .add(GetRiskInstanceActivity)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-risk-actions.js.map